import { motion } from "framer-motion";
import { MessageCircle, Phone, MapPin } from "lucide-react";

// Real client-supplied contact details. Get Directions kept editable
// until actual Google Maps URLs are supplied.
const LOCATIONS = [
  {
    id: "vpe",
    city: "Vile Parle",
    name: "Vile Parle East",
    facility: "PLST Studio & Positive Physiotherapy Clinic",
    lines: [
      "101, Prathamesh Society",
      "Mahant Road, Vile Parle (East)",
      "Mumbai – 400057",
    ],
    tel: "8779853459",
    wa: "918779853459",
    directions: "https://maps.app.goo.gl/8rZhSv9MJx6ost3s9?g_st=ic",
  },
  {
    id: "thw",
    city: "Thane",
    name: "Thane West",
    facility: "PLST Studio & Positive Physiotherapy Clinic",
    lines: [
      "Mangaleshwar Heights, A-1",
      "Opposite Jalaram Traders",
      "Near Our Lady of Fatima Church",
      "Majiwada Village, Tirumala Society",
      "Sainath Nagar, Majiwada",
      "Thane, Maharashtra – 400601",
    ],
    tel: "9833477074",
    wa: "919833477074",
    directions: "https://maps.app.goo.gl/cMeoVUkhSqau7waK8?g_st=ic",
  },
  {
    id: "anw",
    city: "Andheri",
    name: "Andheri West — Lokhandwala",
    facility: "PLST Studio & Positive Physiotherapy Clinic",
    lines: [
      "B-120, Laxmi Business Park",
      "Laxmi Industrial Estate, Suresh Nagar",
      "Andheri (West), Mumbai – 400053",
    ],
    tel: "8369612547",
    wa: "918369612547",
    directions: "https://share.google/vDM8UGb9xXQSRcBpW",
  },
];

function LocationCard({ l }) {
  return (
    <div className="border-t border-[#1E1E1E]/10 pt-8" data-testid={`location-${l.id}`}>
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#A67C00] font-semibold">{l.city}</div>
      <h3 className="mt-2 font-display font-semibold text-2xl lg:text-3xl tracking-tight text-[#1E1E1E]">{l.name}</h3>
      <div className="mt-1 text-sm text-[#1E1E1E]/60">{l.facility}</div>

      <address className="mt-6 not-italic text-sm text-[#1E1E1E]/80 leading-relaxed">
        {l.lines.map((line) => (<div key={line}>{line}</div>))}
        <div className="mt-3">
          <a href={`tel:${l.tel}`} className="text-[#1E1E1E] hover:text-[#A67C00] transition-colors">Tel: {l.tel}</a>
        </div>
      </address>

      <div className="mt-6 flex flex-wrap gap-3">
        <a
          href={`https://wa.me/${l.wa}`}
          target="_blank" rel="noopener noreferrer"
          data-testid={`wa-${l.id}`}
          className="group inline-flex items-center gap-2 bg-[#A67C00] text-[#1E1E1E] px-5 py-3 text-sm font-semibold hover:bg-[#1E1E1E] hover:text-[#F5F4EF] transition-colors"
        >
          <MessageCircle size={16} /> WhatsApp Us <span className="transition-transform group-hover:translate-x-1">→</span>
        </a>
        <a
          href={`tel:${l.tel}`}
          data-testid={`call-${l.id}`}
          className="group inline-flex items-center gap-2 border border-[#1E1E1E]/25 text-[#1E1E1E] px-5 py-3 text-sm hover:border-[#A67C00] hover:text-[#A67C00] transition-colors"
        >
          <Phone size={16} /> Call Us <span className="transition-transform group-hover:translate-x-1">→</span>
        </a>
        <a
          href={l.directions}
          target="_blank" rel="noopener noreferrer"
          data-testid={`dir-${l.id}`}
          className="group inline-flex items-center gap-2 border border-[#1E1E1E]/25 text-[#1E1E1E] px-5 py-3 text-sm hover:border-[#A67C00] hover:text-[#A67C00] transition-colors"
        >
          <MapPin size={16} /> Get Directions <span className="transition-transform group-hover:translate-x-1">→</span>
        </a>
      </div>
    </div>
  );
}

export default function Contact() {
  return (
    <section id="contact" className="py-24 lg:py-40 bg-[#F5F4EF]" data-testid="contact-section">
      <div className="mx-auto max-w-[1600px] px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
            className="lg:col-span-5 lg:sticky lg:top-28"
          >
            <div className="text-xs uppercase tracking-[0.28em] text-[#8A8A8A]">
              <span className="w-8 h-px bg-[#A67C00] inline-block align-middle mr-3" />
              Contact & Locations
            </div>
            <h2 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
              Three centres. <br />
              <span className="italic font-medium text-[#A67C00]">One philosophy.</span>
            </h2>
            <p className="mt-6 text-[#1E1E1E]/70 max-w-md leading-relaxed">
              Want to know if PLST is right for you? Talk to us. We'll help you figure out where to start.
            </p>
            <div className="mt-6 text-sm text-[#1E1E1E]/70">
              <a href="mailto:positiveclinic@gmail.com" data-testid="contact-email-link" className="hover:text-[#A67C00] transition-colors">
                positiveclinic@gmail.com
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}
            className="lg:col-span-7 space-y-12 lg:space-y-16"
          >
            {LOCATIONS.map((l) => (<LocationCard key={l.id} l={l} />))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
